import { useLayoutEffect, useRef, useState } from "react";

import styles from "./InputRequestPrompt.module.css";
import { RoutineChange } from "./RoutineChange";
import { Button } from "../../components/Button";
import {
  answerInputRequest,
  type ApiInputRequest,
  type ClarifyingQuestion,
} from "../../data/api/chat";
import { ApiError } from "../../data/api/client";
import { interruptTask } from "../../data/api/tasks";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";

export type InputRequestPromptProps = {
  readonly onResolved: () => void;
  readonly request: ApiInputRequest;
  readonly taskId: string;
};

// What the user has picked so far, keyed by question text — the same key the
// response body uses.
type Selection = Readonly<Record<string, ReadonlyArray<string>>>;

// Options are numbered `1..n` on screen and answered by the matching number key
// (chat.md §Answering). Nine is where the hotkeys stop: a two-digit shortcut
// needs a timeout to tell `1` from `12`, and the badge past that point is a
// label rather than a key. Options carry no key on the wire, so the index *is*
// the number (chat.md §Question shape reconciliation).
const HOTKEYS = 9;

// Hotkeys are suppressed while the user is typing — in the composer, or in this
// card's own free-text box — so a digit in an answer never doubles as a
// selection (chat.md §Answering). Same test `useGlobalKeys` applies.
const isTyping = (): boolean => {
  const el = document.activeElement;
  return (
    el instanceof HTMLElement &&
    (el.tagName === "INPUT" ||
      el.tagName === "TEXTAREA" ||
      el.isContentEditable)
  );
};

const isAnswered = (selection: Selection, q: ClarifyingQuestion): boolean =>
  (selection[q.question] ?? []).length > 0;

// Each question numbers its options independently, so a digit needs one
// question to mean something in. That is the first one still unanswered — the
// one the reader is being asked — falling back to the last, so a multi-select
// keeps taking digits after its first pick rather than going dead.
const activeQuestion = (
  request: ApiInputRequest,
  selection: Selection,
): ClarifyingQuestion | undefined =>
  request.questions.find((q) => !isAnswered(selection, q)) ??
  request.questions.at(-1);

// The structured "needs input" card rendered after the agent's last message
// when it paused with a question (chat.md §inputRequired). The user picks an
// option (single or multi) per question, types a free-text reply, or takes the
// escape: Stop, which cancels the request outright.
export const InputRequestPrompt = ({
  onResolved,
  request,
  taskId,
}: InputRequestPromptProps) => {
  const [selected, setSelected] = useState<Selection>({});
  const [freeText, setFreeText] = useState("");
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  // Why the last answer did not go out. The card stays exactly as it was, so
  // the same click retries — but a refusal nobody is told about reads as a
  // dead option, and the agent is paused on this card with nothing else to
  // try. Cleared when the next attempt starts.
  const [failure, setFailure] = useState<string | null>(null);

  const send = (selection: Selection): void => {
    const text = freeText.trim();
    const answers = Object.fromEntries(
      request.questions
        .map(
          (q) =>
            [q.question, (selection[q.question] ?? []).join(", ")] as const,
        )
        .filter(([, v]) => v.length > 0),
    );
    if (busy || (!text && Object.keys(answers).length === 0)) {
      return;
    }
    setBusy(true);
    setFailure(null);
    // free_text wins on the server, so send one or the other.
    void answerInputRequest(
      taskId,
      text
        ? { free_text: text, input_request_public_id: request.public_id }
        : { answers, input_request_public_id: request.public_id },
    )
      .then(onResolved)
      .catch((err: unknown) =>
        setFailure(
          err instanceof ApiError ? err.message : "Couldn't send that answer.",
        ),
      )
      .finally(() => setBusy(false));
  };

  // The pick itself is never refused, not even with an answer already in
  // flight: it is local state, and a click that changes nothing on screen is
  // how a card whose request is still outstanding came to look broken.
  // Submitting is what `send` guards.
  const pick = (q: ClarifyingQuestion, label: string): void => {
    const current = selected[q.question] ?? [];
    const next = !q.multiSelect
      ? [label]
      : current.includes(label)
        ? current.filter((l) => l !== label)
        : [...current, label];
    const selection = { ...selected, [q.question]: next };
    setSelected(selection);
    // A single-select question answers on the pick itself (chat.md §Answering):
    // with one answer to give, a confirm step is a click that says nothing new.
    // What it sends is the whole card, so a request carrying several questions
    // goes out on the pick that completes the set. Free text already typed is
    // what the server would take instead (it wins over `answers`), so that case
    // waits for the explicit Send rather than discarding it on an option click.
    if (
      !q.multiSelect &&
      !freeText.trim() &&
      request.questions.every((x) => isAnswered(selection, x))
    ) {
      send(selection);
    }
  };

  // The escape option is a real protocol action, never a no-op that answers
  // nothing and leaves the agent paused (chat.md §The escape option is a real
  // action). Stop is the cancel: the member-level interrupt cancels the open
  // request and takes the task out of `needs_input`. The other escape — "let me
  // explain instead" — is the free-text box already on the card, which resolves
  // the request the same way, so it needs no button of its own.
  const stop = (): void => {
    if (busy) {
      return;
    }
    setBusy(true);
    setFailure(null);
    void interruptTask(taskId)
      .then(onResolved)
      .catch((err: unknown) =>
        setFailure(
          err instanceof ApiError ? err.message : "Couldn't stop the agent.",
        ),
      )
      .finally(() => setBusy(false));
  };

  const active = activeQuestion(request, selected);

  // Bound on the document, not the card: this is the only answer UI for a
  // paused agent, and the reader has no reason to have clicked it first — so
  // the keys work wherever focus happens to be, short of a text field. The
  // listener is registered once and routed through a ref, so a selection
  // changing does not tear down and rebuild it on every keystroke.
  const onKey = (e: KeyboardEvent): void => {
    if (collapsed || e.altKey || e.ctrlKey || e.metaKey || isTyping()) {
      return;
    }
    // Esc collapses; it never cancels. Cancelling is Stop, above. It is not
    // gated on `busy` — an answer in flight is no reason to hold the card open.
    if (e.key === "Escape") {
      e.preventDefault();
      setCollapsed(true);
      return;
    }
    const option = active?.options.slice(0, HOTKEYS)[Number(e.key) - 1];
    if (active && option) {
      e.preventDefault();
      pick(active, option.label);
    }
  };
  // Layout effects, so the card's keys are live from the commit that puts it on
  // screen rather than from whenever passive effects happen to flush after it.
  // "The card is visible" and "its shortcuts work" have to be the same instant:
  // this is the only answer UI a paused agent has, its footer advertises the
  // digits, and a window in which the card is painted and the keys are dead is
  // a card that lies. The window is sub-frame for a person and wide open for
  // anything dispatching a key the moment the card renders — which is what
  // caught this, as an intermittently-dead hotkey in the tests below.
  const latestKeyHandler = useRef(onKey);
  useLayoutEffect(() => {
    latestKeyHandler.current = onKey;
  });
  useLayoutEffect(() => {
    const listener = (e: KeyboardEvent): void => latestKeyHandler.current(e);
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, []);

  // `Esc` shrinks the card rather than dismissing it: while the task is paused
  // this is the only way to answer, so it always stays reachable (chat.md §`Esc`
  // = collapse, not dismiss).
  if (collapsed) {
    return (
      <button
        className={styles.chip}
        onClick={() => setCollapsed(false)}
        type="button"
      >
        <Icon.Help size={12} /> Tinsmith needs your input · paused
      </button>
    );
  }

  // The live key affordances, per chat.md §Answering: the digits that answer
  // the question in play, Esc, and Enter once an answer has been typed.
  const answerKeys = Math.min(active?.options.length ?? 0, HOTKEYS);
  const typed = freeText.trim();
  const canSend =
    typed.length > 0 || Object.values(selected).some((a) => a.length > 0);
  const hint = [
    answerKeys > 1 ? `1–${answerKeys} to answer` : null,
    answerKeys === 1 ? "1 to answer" : null,
    "Esc to collapse",
    // Conditional because that is the moment the reader is in the box
    // wondering whether typing an answer is the end of the job. Enter sends a
    // pending selection too, but a pick that completes the set already submits
    // on its own, so there is nothing there to advertise.
    typed ? "Enter to send" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={styles.prompt}>
      <div className={styles.head}>
        <Icon.Help size={14} /> Tinsmith needs your input
        {/* The turn is blocked on the reader: nothing more arrives until this
            request resolves (chat.md §"Tinsmith needs your input" UI). */}
        <span className={styles.paused}>· paused</span>
      </div>
      {request.routine_change ? (
        <RoutineChange change={request.routine_change} />
      ) : null}
      {request.questions.map((q) => (
        <div className={styles.question} key={q.question}>
          {q.header ? <div className={styles.qHeader}>{q.header}</div> : null}
          <div className={styles.qText}>{q.question}</div>
          {q.options.length > 0 ? (
            <div className={styles.options}>
              {q.options.map((o, i) => {
                const on = (selected[q.question] ?? []).includes(o.label);
                return (
                  <button
                    aria-keyshortcuts={
                      q === active && i < HOTKEYS ? String(i + 1) : undefined
                    }
                    aria-pressed={q.multiSelect ? on : undefined}
                    className={cx(styles.option, on && styles.optionOn)}
                    key={o.label}
                    onClick={() => pick(q, o.label)}
                    title={o.description}
                    type="button"
                  >
                    {/* The positional badge the hotkey mirrors. Hidden from the
                        accessible name — which stays the answer's own label —
                        because `aria-keyshortcuts` above is what announces it. */}
                    <span aria-hidden className={styles.badge}>
                      {i + 1}
                    </span>
                    {on ? <Icon.Check size={12} /> : null}
                    {o.label}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ))}
      <textarea
        className={styles.freeText}
        onChange={(e) => setFreeText(e.target.value)}
        onKeyDown={(e) => {
          // The keys above are suppressed while typing, so the first Esc in
          // here hands focus back instead of trapping the reader in a box the
          // collapse shortcut cannot reach past. A second one collapses.
          if (e.key === "Escape") {
            e.currentTarget.blur();
            return;
          }
          // Enter sends and Shift+Enter is a newline, the composer's bargain
          // (frontend.md §Keymap). This is the other box in the thread a reader
          // types an answer into, and swallowing Enter made the card look
          // broken: the words sat in the box, nothing went out, and the agent
          // stayed paused with no sign that anything had been typed at it.
          // `isComposing` keeps an IME's own Enter out of it.
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            send(selected);
          }
        }}
        // On a routine approval anything typed declines the change, so the box
        // says so rather than inviting a "yes" that would decline.
        placeholder={
          request.routine_change
            ? "…or decline with a reason"
            : "…or type an answer"
        }
        value={freeText}
      />
      {failure ? (
        <div className={styles.failure} role="alert">
          {failure}
        </div>
      ) : null}
      <div className={styles.actions}>
        <button
          className={styles.escape}
          disabled={busy}
          onClick={stop}
          title="Cancel the question and stop the agent"
          type="button"
        >
          <Icon.Stop size={11} /> Stop
        </button>
        <span className={styles.hint}>{hint}</span>
        {/* The pick itself submits on a single-select card, so this button is
            also where an answer already on its way says so — otherwise a slow
            one leaves the card looking exactly as the click found it. */}
        <Button
          disabled={!canSend || busy}
          onClick={() => send(selected)}
          size="sm"
          variant="accent"
        >
          <Icon.ArrowUp size={13} /> {busy ? "Sending…" : "Send answer"}
        </Button>
      </div>
    </div>
  );
};
